import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { useLanguage } from '@/hooks/use-language';
import { useToast } from '@/hooks/use-toast';

interface Member {
  name: string;
  email: string;
  phone: string;
  groups: string[];
  isAdmin: boolean;
}

interface MemberExportProps {
  members: Member[];
  fileName?: string;
}

export function MemberExport({ members, fileName = 'members-export' }: MemberExportProps) {
  const { t } = useLanguage();
  const { toast } = useToast();

  const exportToCSV = () => {
    try {
      // CSV headers
      const headers = ['Name', 'Email', 'Phone', 'Groups', 'Admin'];
      
      // Convert members to CSV rows
      const rows = members.map(member => [
        member.name,
        member.email,
        member.phone,
        member.groups.join('; '),
        member.isAdmin ? 'Yes' : 'No'
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${fileName}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: t('common.success'),
        description: `${members.length} ${t('members.exported')}`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: t('common.error'),
        description: t('members.export_failed'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={exportToCSV}
      disabled={members.length === 0}
    >
      <Download className="h-4 w-4 mr-2" />
      {t('members.export')} ({members.length})
    </Button>
  );
}
